import { Box, Button, Link, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";

import { QrCode } from "../ui/qr-code.js";

/**
 * The "add this account to your authenticator" step of TOTP enrollment.
 *
 * ONE component for both places that enroll an authenticator — the mandatory
 * gate (`/setup-2fa`, `pages/Setup2faPage.tsx`) and self-service
 * (`/profile`, `pages/profile/profile-page/TwoFactorCard.tsx`). They used to
 * each render their own copy of "here is a base32 blob, type it in", which is
 * how they drifted.
 *
 * **The QR code is the primary path** (2026-09-02). Both surfaces previously
 * showed only the raw base32 secret; every staff member had to hand-type 32
 * characters into a phone, and unspaced base32 reads as gibberish — reported
 * as the app "showing illegal characters". The secret is still shown, because
 * it is the only path that works when the authenticator lives on the same
 * screen as the browser (a password manager) or the camera is unavailable —
 * but it is now the fallback, grouped in fours so it can actually be read and
 * copied verbatim.
 *
 * `otpauthUrl` is optional: portal-api has always returned `otpauth_url`
 * alongside `secret`, but if it ever comes back empty (or too long to encode)
 * the manual key alone still completes enrollment.
 */
export function TotpEnrollmentKey({
  secret,
  otpauthUrl,
}: {
  secret: string;
  otpauthUrl?: string | null;
}): JSX.Element {
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      {otpauthUrl ? (
        <>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Scan this with your authenticator app (1Password, Google
            Authenticator…), then enter the 6-digit code it shows:
          </Typography>
          <Box
            sx={{
              justifySelf: "start",
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              // The QR itself is white; the frame matches so it reads as one
              // card rather than a white square floating on the surface.
              bgcolor: "common.white",
              p: 1.5,
              lineHeight: 0,
            }}
          >
            <QrCode value={otpauthUrl} label="Scan to add this account to your authenticator app" />
          </Box>
        </>
      ) : (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          Add this key to your authenticator app (1Password, Google
          Authenticator…), then enter the 6-digit code it shows:
        </Typography>
      )}

      <ManualKey secret={secret} otpauthUrl={otpauthUrl ?? null} hasQr={Boolean(otpauthUrl)} />
    </Box>
  );
}

/** Base32 in groups of four — the difference between a readable key and a wall. */
export function groupSecret(secret: string): string {
  return (secret.match(/.{1,4}/g) ?? []).join(" ");
}

function ManualKey({
  secret,
  otpauthUrl,
  hasQr,
}: {
  secret: string;
  otpauthUrl: string | null;
  hasQr: boolean;
}): JSX.Element {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      // Copy the RAW secret, never the grouped rendering — some apps reject
      // the spaces we added purely so a human could read it.
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is denied in some webviews / insecure origins. The
      // key stays on screen and selectable, so this is a non-event.
    }
  }

  return (
    <Box sx={{ display: "grid", gap: 0.75 }}>
      {hasQr && (
        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
          Can’t scan it? Enter this setup key by hand instead:
        </Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Box
          component="code"
          sx={{
            flex: "1 1 auto",
            wordBreak: "break-all",
            borderRadius: 0.5,
            bgcolor: (t) => alpha(t.palette.surfaceSoft ?? t.palette.background.default, 0.6),
            px: 1,
            py: 0.75,
            fontFamily: "ui-monospace, monospace",
            fontSize: 13,
            letterSpacing: "0.06em",
            color: "text.primary",
          }}
        >
          {groupSecret(secret)}
        </Box>
        <Button size="small" variant="outlined" onClick={() => void copy()}>
          {copied ? "Copied" : "Copy"}
        </Button>
      </Box>
      {otpauthUrl && (
        <Link
          href={otpauthUrl}
          underline="always"
          sx={{ fontSize: 12, color: "primary.main" }}
        >
          Open in authenticator
        </Link>
      )}
    </Box>
  );
}
